import React from "react";
import {
  IAlertMessageProps,
  IPopupProps,
  MultiSelectList,
  IMultiSelectItem,
} from "component-library";

import { PromiseContainer } from "../../../../../../utils/promise";
import {
  withGlobalContext,
  IGlobalContextProps,
  GlobalContextEventTypes,
} from "../../../../../../contexts/GlobalContext";
import {
  INationalityViewModel,
  FacetViewModel,
  FacetCategoryViewModel,
} from "../../../../models/views";
import { ControlSection, GlobalAlertMessage } from "../../../../../Common";
import { dataTestIds } from "data-testids";

export interface INationalityContent {
  nationalitiesSectionName: string;
  nationalitiesField: string;
  nationalitiesPlaceholder: string;
  nationalityWarning: {
    title: string;
    description: string;
    close: string;
    add: string;
  };
}

interface INationalityProps extends IGlobalContextProps {
  model: INationalityViewModel;
  facet: FacetCategoryViewModel;
  content: INationalityContent;
  handleChanges: (model: INationalityViewModel) => void;
}

interface INationalityState {
  isDisabled: boolean;
  selectedData: IMultiSelectItem<number>[];
}

export class Nationality extends React.Component<
  INationalityProps,
  INationalityState
> {
  private approveAddingPromise: PromiseContainer<undefined> =
    new PromiseContainer<undefined>();
  private nationalitiesFacet: IMultiSelectItem<number>[];
  private maxActiveCount: number;

  constructor(props: INationalityProps) {
    super(props);

    this.maxActiveCount = props.facet.maxActiveCount;
    this.nationalitiesFacet = this.mapFacetsToMultiSelectItems(
      props.facet.facets
    );

    this.state = {
      isDisabled: props.model.nationalities.length >= this.maxActiveCount,
      selectedData: props.model.nationalities.map((nationality) => ({
        name: nationality.name,
        value: nationality.id,
      })),
    };
  }

  public render() {
    const { selectedData, isDisabled } = this.state;
    const { content } = this.props;

    return (
      <ControlSection name={content.nationalitiesSectionName}>
        <div
          className="c-basic-info-form__nationalities"
          data-testid={
            dataTestIds.profile[
              "EditBasicInfo.BasicInfoForm.Nationality.nationalitiesField"
            ]
          }
        >
          <MultiSelectList
            type="autocomplete"
            content={{
              label: content.nationalitiesField,
              placeholder: content.nationalitiesPlaceholder,
            }}
            isDisabled={isDisabled}
            selectedItems={selectedData}
            name="nationalities"
            loadData={this.loadData}
            handleChanges={this.handleChanges}
          />
        </div>
      </ControlSection>
    );
  }

  public componentWillReceiveProps(nextProps: INationalityProps) {
    if (nextProps.facet.facets !== this.props.facet.facets) {
      this.nationalitiesFacet = this.mapFacetsToMultiSelectItems(
        nextProps.facet.facets
      );
    }

    if (nextProps.model.nationalities !== this.props.model.nationalities) {
      this.setState({
        selectedData: nextProps.model.nationalities.map((nationality) => ({
          name: nationality.name,
          value: nationality.id,
        })),
      });
    }
  }

  private mapFacetsToMultiSelectItems = (facets: FacetViewModel[]) =>
    facets.map((facet) => ({ name: facet.name, value: facet.id }));

  private handleChanges = async (selectedList: IMultiSelectItem<number>[]) => {
    if (
      selectedList.length > 1 &&
      selectedList.length > this.state.selectedData.length
    ) {
      this.props.globalContext.notifyListener(
        GlobalContextEventTypes.notifyingGlobalAlert,
        this.getAlertMessage()
      );

      this.approveAddingPromise.setPromise();

      await this.approveAddingPromise.promise;

      this.updateModel(selectedList);
      this.props.globalContext.notifyListener(
        GlobalContextEventTypes.closeAllGlobalAlert
      );
    } else {
      this.updateModel(selectedList);
    }
  };

  private updateModel = (selectedList: IMultiSelectItem<number>[]) => {
    const updatedModel = { ...this.props.model };
    const updatedState = { ...this.state };

    updatedState.isDisabled = selectedList.length >= this.maxActiveCount;
    updatedState.selectedData = selectedList;
    updatedModel.nationalities = selectedList.map((item) => ({
      id: item.value,
      name: item.name,
    }));

    this.setState(updatedState, () => this.props.handleChanges(updatedModel));
  };

  private loadData = (searchString: string) => {
    const result = this.nationalitiesFacet.filter(({ name, value }) => {
      const hasMatch = name
        .toLowerCase()
        .startsWith(searchString.toLowerCase());
      const hasDuplicate = this.props.model.nationalities.some(
        (selectedNationality) => selectedNationality.id === value
      );

      return hasMatch && !hasDuplicate;
    });

    return Promise.resolve(result);
  };

  private getAlertMessage = () => {
    const {
      content: { nationalityWarning },
    } = this.props;

    const alertProps: IAlertMessageProps = {
      icon: "warning",
      texts: {
        title: nationalityWarning.title,
        description: nationalityWarning.description,
      },
      buttons: [
        {
          name: nationalityWarning.add,
          click: () => this.approveAddingPromise.resolve(),
          type: "primary",
        },
      ],
    };

    const popupProps: IPopupProps = {
      width: { lg: 4, md: 6 },
      close: () => {
        this.approveAddingPromise.reject();
      },
      priority: "high",
      texts: { closePopup: nationalityWarning.close },
    };

    const setting: GlobalAlertMessage = {
      popupProps: popupProps,
      alertMessageProps: alertProps,
      closeButton: {
        name: nationalityWarning.close,
        type: "secondary",
        click: () => {
          this.approveAddingPromise.reject();
        },
      },
    };

    return setting;
  };
}

export default withGlobalContext(Nationality);
